import { HttpBackend, HttpClient } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap } from 'rxjs';

import { AuthService } from '../auth.service';
import { ConfigService } from '../config.service';
import { CoreApiService } from '../core-api.service';
import { LoggingService } from '../logging.service';

interface LDAPAuthResponse {
  token: string;
  full_name: string;
}

@Component({
  selector: 'login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent {

  private unguardedHttp: HttpClient;

  features = {
    ldap: this.config.apiConfig?.auth?.ldap || false,
    basicAuth: this.config.apiConfig?.auth?.basic?.enabled || false,
  }

  ldapUserOverride = this.config.apiConfig?.auth?.ldap?.username_field_label;

  loginError: string;

  authForm = new FormGroup({
    username: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
  });

  activeAuthMethod: 'ldap' | 'basic' | null = null;

  constructor(
    private auth: AuthService,
    private config: ConfigService,
    private coreApi: CoreApiService,
    private router: Router,
    private route: ActivatedRoute,
    private httpBackend: HttpBackend,
    private logger: LoggingService,
  ) {
    this.unguardedHttp = new HttpClient(httpBackend);
  }

  loginWithLdap(): void {
    this.loginError = null;
    const login = this.authForm.value;
    this.unguardedHttp.post<LDAPAuthResponse>(this.config.apiConfig.auth.ldap.auth_endpoint, login).pipe(
      switchMap(response => 
        this.unguardedHttp.post<{ token: string}>(this.config.getApiUrl('/api/login'), {
          auth_type: 'ldap',
          code: response.token,
        })
      )
    ).subscribe({
      next: (response) => {
        this.auth.setToken(response.token);
        this.redirect();
      },
      error: (err) => {
        this.loginError = err.error?.message || 'Login failed';
        this.logger.warn(err.error);
      },
    });
  }

  loginWithBasicAuth(): void {
    this.loginError = null;
    const login = this.authForm.value;
    this.unguardedHttp.post<{ token: string }>(this.config.getApiUrl('/api/login/basic'),
      { email: login.username, password: login.password }
    ).subscribe({
      next: (response) => {
        this.auth.setToken(response.token);
        this.redirect();
      },
      error: (err) => {
        this.loginError = err.error?.message || 'Login failed';
        this.logger.warn(err.error);
      },
    });
  }

  redirect(): void {
    if (this.route.snapshot.queryParams.redirect) {
      this.router.navigate([this.route.snapshot.queryParams.redirect]);
    } else {
      this.router.navigate(['/']);
    }
  }
}